import React, { useMemo } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
  Pressable,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../theme";
import { ThemeColors } from "../theme/types";

interface LHConfirmationDialogProps {
  visible: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  message: string;
  useHighContrastMessage?: boolean;
}

const LHConfirmationDialog: React.FC<LHConfirmationDialogProps> = ({
  visible,
  onConfirm,
  onCancel,
  message,
  useHighContrastMessage = false,
}) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.modalOverlay}>
        <TouchableWithoutFeedback onPress={onCancel}>
          <View style={styles.modalBackground} />
        </TouchableWithoutFeedback>

        <View style={styles.overlayContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Confirmation</Text>
            <Pressable style={styles.closeButton} onPress={onCancel}>
              <MaterialIcons name="close" size={24} color={colors.text.primary} />
            </Pressable>
          </View>

          {/* Body */}
          <View style={styles.bodyContainer}>
            <Text
              style={[
                styles.message,
                useHighContrastMessage && { color: colors.text.primary },
              ]}
            >
              {message}
            </Text>
          </View>

          {/* Footer with Buttons */}
          <View style={styles.footer}>
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.secondaryButton]}
                onPress={onCancel}
              >
                <Text style={styles.secondaryButtonText}>No</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.primaryButton]}
                onPress={onConfirm}
              >
                <Text style={styles.primaryButtonText}>Yes</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    modalOverlay: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      padding: 20,
    },
    closeButton: {
      padding: 8,
    },
    modalBackground: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.overlay,
    },
    overlayContainer: {
      backgroundColor: colors.background.surface,
      borderWidth: 1,
      borderColor: colors.border.default,
      borderRadius: 8,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
      width: "90%",
      maxWidth: 400,
      overflow: "hidden",
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border.divider,
    },
    title: {
      fontSize: 20,
      fontWeight: "600",
      color: colors.text.primary,
      flex: 1,
    },
    bodyContainer: {
      paddingHorizontal: 24,
      paddingVertical: 16,
    },
    message: {
      fontSize: 16,
      lineHeight: 24,
      color: colors.text.primary,
      textAlign: "center",
    },
    footer: {
      padding: 16,
    },
    buttonContainer: {
      flexDirection: "row",
      justifyContent: "center",
      gap: 12,
    },
    button: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 20,
      alignItems: "center",
      justifyContent: "center",
      minWidth: 100,
    },
    primaryButton: {
      backgroundColor: colors.brand.primary,
    },
    secondaryButton: {
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: colors.brand.primary,
    },
    primaryButtonText: {
      color: colors.text.inverse,
      fontSize: 16,
      fontWeight: "600",
    },
    secondaryButtonText: {
      color: colors.brand.primary,
      fontSize: 16,
      fontWeight: "600",
    },
  });

export default LHConfirmationDialog;
